import { z } from 'zod';
import { SubjectSchema } from '../common/schemas';
import { DBStudentStudentIdSchema } from '../students/schemas';
import { DEFAULT_SUBJECT_ANALYTICS_MARKS_TALLY } from './constants';

/**
 * Shared & Helper Schemas
 */

const DBStudentSubjectAnalyticsMarksTallySchema = z.object({
  attemptedMarks: z.number().int().min(0),
  inaccuracyMarks: z.number().int().min(0),
  missedMarks: z.number().int().min(0),
  sillyMarks: z.number().int().min(0),
});

/**
 * DB Schemas
 */

export const DBStudentSubjectAnalyticsStudentSchema = z.lazy(() => DBStudentStudentIdSchema);
export const DBStudentSubjectAnalyticsSubjectTopicIdentifierSchema = z.string().refine(val => {
  const parts = val.split('#');
  return (
    parts.length === 2 &&
    z.literal('SUBJECT').safeParse(parts[0]).success &&
    z.lazy(() => SubjectSchema).safeParse(parts[1]).success
  );
});
export const DBStudentSubjectAnalyticsAnalyticsSchema = DBStudentSubjectAnalyticsMarksTallySchema;
export const DBStudentSubjectAnalyticsCalculationAnalyticsSchema =
  DBStudentSubjectAnalyticsMarksTallySchema;
export const DBStudentSubjectAnalyticsExtendedResponseAnalyticsSchema =
  DBStudentSubjectAnalyticsMarksTallySchema;
export const DBStudentSubjectAnalyticsGraphingAnalyticsSchema =
  DBStudentSubjectAnalyticsMarksTallySchema;
export const DBStudentSubjectAnalyticsMultipleChoiceAnalyticsSchema =
  DBStudentSubjectAnalyticsMarksTallySchema;
export const DBStudentSubjectAnalyticsProofAnalyticsSchema =
  DBStudentSubjectAnalyticsMarksTallySchema;
export const DBStudentSubjectAnalyticsShortAnswerAnalyticsSchema =
  DBStudentSubjectAnalyticsMarksTallySchema;

export const DBStudentSubjectAnalyticsSchema = z.object({
  student: DBStudentSubjectAnalyticsStudentSchema,
  subjectTopicIdentifier: DBStudentSubjectAnalyticsSubjectTopicIdentifierSchema,
  analytics: DBStudentSubjectAnalyticsAnalyticsSchema,
  calculationAnalytics: DBStudentSubjectAnalyticsCalculationAnalyticsSchema.optional(),
  extendedResponseAnalytics: DBStudentSubjectAnalyticsExtendedResponseAnalyticsSchema.optional(),
  graphingAnalytics: DBStudentSubjectAnalyticsGraphingAnalyticsSchema.optional(),
  multipleChoiceAnalytics: DBStudentSubjectAnalyticsMultipleChoiceAnalyticsSchema.optional(),
  proofAnalytics: DBStudentSubjectAnalyticsProofAnalyticsSchema.optional(),
  shortAnswerAnalytics: DBStudentSubjectAnalyticsShortAnswerAnalyticsSchema.optional(),
});

/**
 * Client Schemas
 */

const StudentSubjectAnalyticsMarksTallySchema = z.object({
  attemptedMarks: z.number().int().min(0).default(0),
  inaccuracyMarks: z.number().int().min(0).default(0),
  missedMarks: z.number().int().min(0).default(0),
  sillyMarks: z.number().int().min(0).default(0),
});

export const StudentSubjectAnalyticsStudentIdSchema = DBStudentSubjectAnalyticsStudentSchema;
export const StudentSubjectAnalyticsSubjectSchema = z.lazy(() => SubjectSchema);
export const StudentSubjectAnalyticsAnalyticsSchema = StudentSubjectAnalyticsMarksTallySchema;
export const StudentSubjectAnalyticsCalculationAnalyticsSchema =
  StudentSubjectAnalyticsMarksTallySchema;
export const StudentSubjectAnalyticsExtendedResponseAnalyticsSchema =
  StudentSubjectAnalyticsMarksTallySchema;
export const StudentSubjectAnalyticsGraphingAnalyticsSchema =
  StudentSubjectAnalyticsMarksTallySchema;
export const StudentSubjectAnalyticsMultipleChoiceAnalyticsSchema =
  StudentSubjectAnalyticsMarksTallySchema;
export const StudentSubjectAnalyticsProofAnalyticsSchema = StudentSubjectAnalyticsMarksTallySchema;
export const StudentSubjectAnalyticsShortAnswerAnalyticsSchema =
  StudentSubjectAnalyticsMarksTallySchema;

export const StudentSubjectAnalyticsSchema = z.object({
  studentId: StudentSubjectAnalyticsStudentIdSchema,
  subject: StudentSubjectAnalyticsSubjectSchema,
  analytics: StudentSubjectAnalyticsAnalyticsSchema.default(DEFAULT_SUBJECT_ANALYTICS_MARKS_TALLY),
  calculationAnalytics: StudentSubjectAnalyticsCalculationAnalyticsSchema.optional(),
  extendedResponseAnalytics: StudentSubjectAnalyticsExtendedResponseAnalyticsSchema.optional(),
  graphingAnalytics: StudentSubjectAnalyticsGraphingAnalyticsSchema.optional(),
  multipleChoiceAnalytics: StudentSubjectAnalyticsMultipleChoiceAnalyticsSchema.optional(),
  proofAnalytics: StudentSubjectAnalyticsProofAnalyticsSchema.optional(),
  shortAnswerAnalytics: StudentSubjectAnalyticsShortAnswerAnalyticsSchema.optional(),
});
